import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import type { Event } from "@event-planner/shared";

/**
 * Top N events by `updatedAt` desc — used by the event switcher's "Recent"
 * section. Not per-user visit history (which would need a separate
 * collection); `updatedAt` is a reasonable proxy for "what the organizer is
 * working on lately" since editing an event bumps it to the top of the list.
 *
 * Uses `onSnapshot` (deliberate exception to ADR-019's one-time-fetch policy):
 * the sidebar mounts once at admin app load and lives across every route, so
 * a getDocs-on-mount snapshot goes stale the moment any event is
 * created/edited from elsewhere in the portal. A 5-doc live subscription is
 * cheap and matches user expectation that a freshly-saved event appears in
 * the picker without a hard refresh.
 */
export function useRecentEvents(max: number = 5) {
  const [data, setData] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const recentQuery = query(
      collection(db, "events"),
      orderBy("updatedAt", "desc"),
      limit(max),
    );
    const unsub = onSnapshot(
      recentQuery,
      (snapshot) => {
        setData(
          snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() }) as Event,
          ),
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [max]);

  return { data, loading, error };
}
